import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { SidebarOpenIcon } from 'lucide-react';
import TableComponent, { type Column } from '@/components/TableComponent';
import AlertDialog from '@/components/AlertDialog';
import { StatusChip as BrandChip } from '@/components/GenericDeviceCard/styles';
import { getRouterColor } from '@/utils/brandColors';
import { StatusChip } from '../styles';
import { getRowStyle } from './styles';
import api from '@/services/api';

export type DisplayMode = 'list' | 'grid';
interface ResultsTableProps {
  loading: boolean;
  data: DeviceType[];
  // Devices matching the search, across every page
  totalCount: number;
  // Page size requested from the server
  rowsPerPage: number;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  onDisplayModeChange?: (
    event: React.MouseEvent<HTMLElement>,
    newValue: DisplayMode | null
  ) => void;
  devicesInUse: Record<string | number, boolean>;
  onUseModeChange: (
    deviceId: string | number,
    newInUseStatus: boolean | null
  ) => void;
  bulkMode: boolean;
  bulkAction: 'enable' | 'disable' | null;
  stagedInUse: Record<string | number, boolean>;
  onDeviceSelect: (deviceId: string | number) => void;
}

/** Displays equipments researched on list mode */
const ResultsTable: React.FC<ResultsTableProps> = ({
  loading,
  data,
  totalCount,
  rowsPerPage,
  page,
  setPage,
  devicesInUse,
  onUseModeChange,
  bulkMode,
  bulkAction,
  stagedInUse,
  onDeviceSelect,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false); // controls visibility of the confirmation dialog (individual inUse change)
  const [pendingChange, setPendingChange] = useState<{
    deviceId: string | number;
    newValue: boolean;
  } | null>(null);

  // if in bulk mode, do nothing (toggles are disabled), user must use the bulk action button
  const handleToggleClick = useCallback(
    (deviceId: string | number, newValue: boolean | null) => {
      if (newValue === null || bulkMode) return;
      setPendingChange({ deviceId, newValue });
      setConfirmDialogOpen(true);
    },
    [bulkMode]
  );

  // call when user confirms the change in the AlertDialog
  const handleConfirm = useCallback(async () => {
    if (pendingChange) {
      try {
        const response = await api.patch(
          `/devices/${pendingChange.newValue ? 'enable' : 'disable'}`,
          [pendingChange.deviceId]
        );
        if (response.status == 204)
          onUseModeChange(pendingChange.deviceId, pendingChange.newValue);
      } catch (error) {
        console.error(t('common.error'), error);
      }
    }
    setPendingChange(null);
  }, [pendingChange, onUseModeChange]);

  const columnsTable: Column<DeviceType>[] = useMemo(() => {
    const checkboxCol: Column<DeviceType>[] = bulkMode
      ? [
          {
            id: '_select' as keyof DeviceType,
            label: '',
            align: 'center' as const,
            render: (_value: unknown, row: DeviceType) => (
              <Checkbox
                checked={row.id in stagedInUse}
                onChange={(e) => {
                  e.stopPropagation();
                  onDeviceSelect(row.id);
                }}
                onClick={(e) => e.stopPropagation()}
                color={bulkAction === 'enable' ? 'primary' : 'error'}
                size="small"
              />
            ),
          },
        ]
      : [];

    return [
      ...checkboxCol,
      {
        id: 'inUse',
        label: t('listEquipments.table.inUse'),
        render: (_value: unknown, row: DeviceType) => {
          const currentValue = bulkMode
            ? (stagedInUse[row.id] ?? false)
            : (devicesInUse[row.id] ?? false);

          return (
            <Checkbox
              checked={currentValue}
              disabled={bulkMode}
              onClick={(e) => e.stopPropagation()}
              onChange={(_, checked) => {
                handleToggleClick(row.id, checked);
              }}
            />
          );
        },
      },
      {
        id: 'online',
        label: t('listEquipments.table.online'),
        render: (value: unknown) => (
          <StatusChip
            value={Boolean(value)}
            label={
              value
                ? t('listEquipments.filter.online').toUpperCase()
                : t('listEquipments.filter.offline').toUpperCase()
            }
          />
        ),
      },
      {
        id: 'customerName',
        label: t('listEquipments.table.customerName'),
        render: (_value: unknown, row: DeviceType) => {
          const params = row.customerData;
          if (params && typeof params === 'object' && 'customerName' in params)
            return (params as CustomerDataType).customerName;
          else return t('listEquipments.table.unknown').toUpperCase();
        },
      },
      {
        id: 'userPPP',
        label: t('listEquipments.table.userPPP'),
        render: (_value: unknown, row: DeviceType) => {
          const params = row.customerData;
          if (params && typeof params === 'object' && 'userPPP' in params)
            return (params as CustomerDataType).userPPP || '-';
          else return '-';
        },
      },
      {
        id: 'manufacturer',
        label: t('listEquipments.table.manufacturer'),
        render: (value: unknown) =>
          value ? (
            <BrandChip
              label={String(value)}
              size="small"
              $bgColor={getRouterColor(String(value))}
              $textColor={theme.palette.getContrastText(
                getRouterColor(String(value))
              )}
            />
          ) : (
            '-'
          ),
      },
      {
        id: 'productClass',
        label: t('listEquipments.table.modelAlias'),
        render: (_value: unknown, row: DeviceType) =>
          row.model?.alias || row.productClass,
      },
      { id: 'ipAddress', label: t('listEquipments.table.ipAddress') },
      {
        id: 'lanMac',
        label: t('listEquipments.table.lanMac'),
        render: (_value: unknown, row: DeviceType) =>
          row.customerData?.lanMacAddress || '-',
      },
      { id: 'serialNumber', label: t('listEquipments.table.serialNumber') },
      {
        id: 'softwareVersion',
        label: t('listEquipments.table.softwareVersion'),
        render: (_value: unknown, row: DeviceType) =>
          row.softwareVersion || '-',
      },
      {
        id: 'id',
        label: t('listEquipments.table.actions'),
        render: (params: unknown) => (
          <div>
            <Link to={`/equipment/${String(params)}`}>
              <IconButton size="small">
                <SidebarOpenIcon size={18} />
              </IconButton>
            </Link>
          </div>
        ),
      },
    ];
  }, [
    t,
    devicesInUse,
    stagedInUse,
    bulkMode,
    bulkAction,
    handleToggleClick,
    onDeviceSelect,
  ]);

  if (loading) {
    return (
      <>
        <Skeleton variant="rounded" height={75} sx={{ mb: 2 }} />
        {[...Array(3)].map((_, index) => (
          <Skeleton key={index} variant="rounded" height={75} sx={{ mb: 2 }} />
        ))}
      </>
    );
  }

  return (
    <Stack>
      <TableComponent
        data={data}
        columns={columnsTable}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        serverPagination
        totalCount={totalCount}
        handleClickRow={() => {}}
        getRowSx={(row: DeviceType) =>
          getRowStyle(theme, row, bulkMode, stagedInUse)
        }
      />

      {!loading && data.length === 0 && (
        <TableContainer sx={{ width: '100%' }}>
          <Table sx={{ width: '100%' }}>
            <TableBody>
              <TableRow>
                <TableCell colSpan={10} align="center">
                  {t(
                    'listEquipments.noEquipments',
                    'Nenhum equipamento encontrado'
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/*individual dialog confirmation to change inUse value */}
      <AlertDialog
        title={t('common.confirmAction', 'Confirmar alteração')}
        content={
          <Typography>
            {pendingChange?.newValue
              ? t(
                  'listEquipments.confirmInUseEnable',
                  'Deseja realmente ativar o uso deste equipamento?'
                )
              : t(
                  'listEquipments.confirmInUseDisable',
                  'Deseja realmente desativar o uso deste equipamento?'
                )}
          </Typography>
        }
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        setValue={handleConfirm}
      />
    </Stack>
  );
};

export default ResultsTable;
